<template>
  <component
    :is="tag"
    :aria-busy="props.loading || undefined"
    :class="[
      'relative inline-flex select-none items-center justify-center gap-2 rounded-full text-center text-action',
      'pj-tap',
      // Keyboard focus, stated on the component rather than left to the global
      // rule: this is the control most often reached by tab, and the ring has
      // to clear a green fill as well as a white page.
      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
      props.icon ? 'aspect-square min-h-btn-xs px-0' : SIZE_CLASS[props.size ?? 'md'],
      props.block && 'w-full',
      isInert ? 'cursor-default bg-line text-disabled' : VARIANT_CLASS[props.variant ?? 'filled']
    ]"
    :disabled="props.href ? undefined : props.disabled || props.loading"
    :href="props.href"
    :rel="props.external ? 'noopener' : undefined"
    :target="props.external ? '_blank' : undefined"
    :type="props.href ? undefined : (props.type ?? 'button')"
  >
    <!-- inline-flex, not a bare span: preflight renders <svg> as a block, so
         an icon in the slot would break to its own line. The label also keeps
         its space while loading, so the button cannot resize under a thumb
         already on the way down. -->
    <span :class="['inline-flex items-center gap-2', props.loading && 'invisible']">
      <slot />
    </span>

    <span
      v-if="props.loading"
      class="absolute inset-0 flex items-center justify-center gap-1.5"
    >
      <span
        v-for="dot in 3"
        :key="dot"
        class="h-1.5 w-1.5 rounded-full bg-current motion-safe:animate-pulse"
        :style="{ animationDelay: `${(dot - 1) * 140}ms` }"
      />
    </span>
  </component>
</template>

<script lang="ts" setup>
import { Link } from '@inertiajs/vue3'
import { computed } from 'vue'

import type {
  PjButtonProps,
  PjButtonSize,
  PjButtonVariant
} from '@/domains/pjazza/components/atoms/pj-button/PjButton.types.ts'

/**
 * The only button in the app. A pill, at rest and pressed alike.
 *
 * No shape morph: the radius animation drew the eye to the control rather than
 * to what the control does. Feedback is a change of fill — the tonal step the
 * ramp already provides — plus the same small press (`pj-tap`) every control in
 * the app now shares, so a button is no longer the one thing on screen that moves.
 *
 * Filled carries the brand — blue in light, pink in dark — and there may be one
 * per screen; that is the colour budget, not a preference. Anything else that
 * needs to look like an action is tonal, outlined or text, none of which spend it.
 *
 * A disabled button gets no hover at all: a control that responds to a pointer
 * has promised something.
 */
const props = defineProps<PjButtonProps>()

const isInert = computed(() => props.disabled || props.loading)

const tag = computed(() => {
  if (!props.href) return 'button'

  return props.external ? 'a' : Link
})

// Horizontal padding is roughly half the height — a pill needs generous sides
// or the label crowds the curve. `icon` bypasses this entirely: a square holding
// one glyph wants no side padding at all.
const SIZE_CLASS: Record<PjButtonSize, string> = {
  xs: 'min-h-btn-xs px-btn-x-xs',
  md: 'min-h-btn-md px-btn-x-md',
  lg: 'min-h-btn-lg px-btn-x-lg'
}

const VARIANT_CLASS: Record<PjButtonVariant, string> = {
  // One tone deeper in the same family, which is what surfaceTint is for.
  filled: 'bg-primary text-on-primary hover:bg-primary-hover active:bg-primary-hover',
  // The neutral control: the card fill — white in light, a lifted slate in dark
  // — set off the page by the one hairline you can see. A container fill
  // alone is ~1.1:1 on this tinted page and simply vanishes, which is why the
  // POS puts a white button on the same ground; here the edge, not the fill, is
  // what makes it a control. Hover warms the fill a hair; press settles it.
  tonal: 'border border-line bg-card text-ink hover:bg-surface-faint active:bg-surface',
  // The secondary beside a filled one: the brand's own quiet container — pale in
  // light, deep in dark — so it reads as an action without spending the colour
  // budget the filled one holds.
  soft: 'bg-container text-on-container hover:brightness-95 active:brightness-90',
  outlined: 'border border-outline bg-transparent text-primary-hover hover:bg-surface-faint active:bg-container',
  text: 'bg-transparent text-primary-hover hover:bg-surface-faint active:bg-container'
}
</script>
